import api from './api';
import { 
  createRecordingDto, 
  createTrimAndCropDto, 
  createCheckoutSessionResponse, 
  createProcessingInfoDto,
  isValidRecordingDto,
  isValidTrimAndCropDto,
  PROCESSING_STATUS,
  ERROR_TYPES
} from '../types/recording';

/**
 * Recording API utilities that match the backend RecordingController
 * Enhanced with better error handling and type safety
 */

// Response parsing utilities
export const unwrap = (response) => {
  console.log('🔍 unwrap - Input Response:', response);
  const data = response.data;
  console.log('🔍 unwrap - Extracted Data:', data);
  const unwrapped = data.data || data;
  console.log('🔍 unwrap - Final Unwrapped:', unwrapped);
  return unwrapped;
};

export const parseRecordingResponse = (response) => {
  console.log('🔍 parseRecordingResponse - Raw Response:', response);
  const unwrapped = unwrap(response);
  
  // Validate and transform recording data
  const content = (unwrapped.content || []).map(recording => {
    const validatedRecording = createRecordingDto(recording);
    if (!isValidRecordingDto(validatedRecording)) {
      console.warn('Invalid recording data:', recording);
    }
    return validatedRecording;
  });
  
  return {
    content,
    pageable: unwrapped.pageable || {},
    last: unwrapped.last || false,
    totalElements: unwrapped.totalElements || 0,
    totalPages: unwrapped.totalPages || 0,
    size: unwrapped.size || 20,
    number: unwrapped.number || 0,
    first: unwrapped.first || true,
    numberOfElements: unwrapped.numberOfElements || 0,
    empty: unwrapped.empty || false
  };
};

export const parseCheckoutResponse = (response) => {
  const unwrapped = unwrap(response);
  return createCheckoutSessionResponse({
    sessionId: unwrapped.id,
    sessionUrl: unwrapped.url,
    amountInCents: unwrapped.amountInCents,
    currency: unwrapped.currency,
    errorMessage: unwrapped.msg || unwrapped.errorMessage // Backend uses 'msg' field
  });
};

export const parseProcessingStatusResponse = (response) => {
  const unwrapped = unwrap(response);
  return createProcessingInfoDto(unwrapped);
};

// Enhanced error handling utility
const handleApiError = (error, operation) => {
  console.error(`Error in ${operation}:`, error);
  
  if (error.response) {
    // Server responded with error status
    const { status, data } = error.response;
    const errorMessage = data?.message || data?.error || `HTTP ${status}`;
    
    switch (status) {
      case 401:
        throw new Error('Unauthorized access. Please log in again.');
      case 403:
        throw new Error('Access denied. You do not have permission for this action.');
      case 404:
        throw new Error('Recording not found.');
      case 409:
        throw new Error('Conflict: Recording may be in use or already processed.');
      case 500:
        throw new Error('Server error. Please try again later.');
      default:
        throw new Error(errorMessage);
    }
  } else if (error.request) {
    // Network error
    throw new Error('Network error. Please check your connection and try again.');
  } else {
    // Other error
    throw new Error(error.message || 'An unexpected error occurred.');
  }
};

// Recording API functions with enhanced error handling
export const getAllRecordings = async (page = 0, size = 20) => {
  try {
    const response = await api.get('/recording', {
      params: { page, size }
    });
    return parseRecordingResponse(response);
  } catch (error) {
    handleApiError(error, 'fetching all recordings');
  }
};

export const getRecordingUrl = async (livestreamId) => {
  try {
    const response = await api.get(`/recording/${livestreamId}`);
    return response.data;
  } catch (error) {
    handleApiError(error, 'fetching recording URL');
  }
};

export const purchaseRecording = async (recordingId, discountCode = null) => {
  try {
    // Validate input
    if (!recordingId) {
      throw new Error('Recording ID is required');
    }

    const requestData = {};
    if (discountCode && discountCode.trim()) {
      requestData.discountCode = discountCode.trim();
    }

    const response = await api.post(`/recording/${recordingId}/purchase`, requestData);
    return parseCheckoutResponse(response);
  } catch (error) {
    handleApiError(error, 'purchasing recording');
  }
};

export const downloadRecording = async (recordingId, filename = null) => {
  try {
    if (!recordingId) {
      throw new Error('Recording ID is required');
    }

    const response = await api.get(`/recording/${recordingId}/download`, {
      responseType: 'blob'
    });

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename || `recording-${recordingId}.mp4`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return response;
  } catch (error) {
    handleApiError(error, 'downloading recording');
  }
};

export const renameRecording = async (recordingId, newTitle) => {
  try {
    if (!recordingId) {
      throw new Error('Recording ID is required');
    }
    if (!newTitle || !newTitle.trim()) {
      throw new Error('New title is required');
    }

    await api.put(`/recording/${recordingId}/rename`, null, {
      params: { newTitle: newTitle.trim() }
    });
    return true;
  } catch (error) {
    handleApiError(error, 'renaming recording');
  }
};

export const deleteRecording = async (recordingId) => {
  try {
    if (!recordingId) {
      throw new Error('Recording ID is required');
    }

    await api.delete(`/recording/${recordingId}`);
    return true;
  } catch (error) {
    handleApiError(error, 'deleting recording');
  }
};

export const trimCropRecording = async (recordingId, trimCropData) => {
  try {
    if (!recordingId) {
      throw new Error('Recording ID is required');
    }
    if (!isValidTrimAndCropDto(trimCropData)) {
      throw new Error('Invalid trim/crop data provided');
    }

    const validatedData = createTrimAndCropDto(trimCropData);
    const response = await api.post(`/recording/${recordingId}/trim-crop`, validatedData);
    return response.data; // Returns UUID tracking ID
  } catch (error) {
    handleApiError(error, 'starting trim/crop operation');
  }
};

export const getProcessingStatus = async (trackingId) => {
  try {
    if (!trackingId) {
      throw new Error('Tracking ID is required');
    }

    const response = await api.get('/recording/processing-status', {
      params: { trackingId }
    });
    return parseProcessingStatusResponse(response);
  } catch (error) {
    handleApiError(error, 'fetching processing status');
  }
};

// Enhanced monitoring function for processing status
export const monitorProcessingStatus = async (trackingId, onStatusUpdate, maxAttempts = 60) => {
  let attempts = 0;
  
  const pollStatus = async () => {
    try {
      const status = await getProcessingStatus(trackingId);
      onStatusUpdate(status);
      
      if (status.status === PROCESSING_STATUS.COMPLETED || 
          status.status === PROCESSING_STATUS.FAILED) {
        return status;
      }
      
      if (attempts >= maxAttempts) {
        throw new Error('Processing timeout - maximum attempts reached');
      }
      
      attempts++;
      setTimeout(pollStatus, 2000); // Poll every 2 seconds
    } catch (error) {
      onStatusUpdate({ 
        status: PROCESSING_STATUS.FAILED, 
        errorMessage: error.message 
      });
      throw error;
    }
  };
  
  return pollStatus();
};

// Utility functions for recording data
export const formatRecordingDuration = (durationSeconds) => {
  if (!durationSeconds) return '0:00';
  
  const hours = Math.floor(durationSeconds / 3600);
  const minutes = Math.floor((durationSeconds % 3600) / 60);
  const seconds = durationSeconds % 60;
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

export const formatFileSize = (bytes) => {
  if (!bytes) return '0 B';
  
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${sizes[i]}`;
};

export const getThumbnailUrl = (recording) => {
  console.log('🔍 getThumbnailUrl - Recording:', recording);
  console.log('🔍 getThumbnailUrl - Thumbnails:', recording.thumbnails);
  
  if (recording.thumbnails && Array.isArray(recording.thumbnails) && recording.thumbnails.length > 0) {
    const firstThumbnail = recording.thumbnails[0];
    console.log('🔍 getThumbnailUrl - First Thumbnail:', firstThumbnail);
    
    if (firstThumbnail && firstThumbnail.url) {
      console.log('🔍 getThumbnailUrl - Using URL:', firstThumbnail.url);
      return firstThumbnail.url;
    }
  }
  
  console.log('🔍 getThumbnailUrl - No thumbnail found, using fallback');
  return '/fallback-thumbnail.png';
};